import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { Production } from '../types'

interface UseProductionsResult {
  productions: Production[]
  loading: boolean
  error: string | null
}

interface UseSingleProductionResult {
  production: Production | null
  loading: boolean
  error: string | null
}

export function useProductionsByPlay(playId: string): UseProductionsResult {
  const [productions, setProductions] = useState<Production[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!playId) {
      setLoading(false)
      return
    }

    async function fetchProductions() {
      setLoading(true)
      setError(null)

      const { data, error: supabaseError } = await supabase
        .from('productions')
        .select('*')
        .eq('play_id', playId)
        .order('year', { ascending: true })

      if (supabaseError) {
        setError(supabaseError.message)
      } else {
        setProductions(data ?? [])
      }

      setLoading(false)
    }

    fetchProductions()
  }, [playId])

  return { productions, loading, error }
}

export function useProduction(id: string): UseSingleProductionResult {
  const [production, setProduction] = useState<Production | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) {
      setLoading(false)
      return
    }

    async function fetchProduction() {
      setLoading(true)
      setError(null)

      const { data, error: supabaseError } = await supabase
        .from('productions')
        .select('*')
        .eq('id', id)
        .maybeSingle()

      if (supabaseError) {
        setError(supabaseError.message)
      } else {
        setProduction(data)
      }

      setLoading(false)
    }

    fetchProduction()
  }, [id])

  return { production, loading, error }
}
